import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid2 as Grid,
  Typography,
  Divider,
  Box,
  Alert,
} from '@mui/material';
import { placeSchema } from '../../schemas/validationSchemas';

/**
 * Formulário de Local (Create/Update) com validação Zod
 */
// focusField: campo que recebe o foco quando a janela termina de abrir (ex.: 'addressStreet'). A tela
// de Locais abre a edição "já no campo que falta" — sem ele, o foco fica no nome, como sempre foi.
export default function PlaceForm({ open, onClose, onSubmit, data, loading = false, focusField = null }) {
  const isEdit = !!data;

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(placeSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      email: '',
      phone: '',
      website: '',
      addressStreet: '',
      addressNumber: '',
      addressComplement: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
      addressZipCode: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (data) {
        reset({
          name: data.name || '',
          description: data.description || '',
          email: data.email || '',
          phone: data.phone || '',
          website: data.website || '',
          addressStreet: data.addressStreet || '',
          addressNumber: data.addressNumber || '',
          addressComplement: data.addressComplement || '',
          addressNeighborhood: data.addressNeighborhood || '',
          addressCity: data.addressCity || '',
          addressState: data.addressState || '',
          addressZipCode: data.addressZipCode || '',
        });
      } else {
        reset({
          name: '',
          description: '',
          email: '',
          phone: '',
          website: '',
          addressStreet: '',
          addressNumber: '',
          addressComplement: '',
          addressNeighborhood: '',
          addressCity: '',
          addressState: '',
          addressZipCode: '',
        });
      }
    }
  }, [data, open, reset]);

  const onSubmitForm = (formData) => {
    onSubmit(formData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEntered: () => { if (focusField) setFocus(focusField); } }}
    >
      <DialogTitle>
        {isEdit ? 'Editar Local' : 'Novo Local'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Informações Básicas */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Informações Básicas
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    autoFocus={!focusField}
                    label="Nome do Local"
                    fullWidth
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Descrição"
                    fullWidth
                    multiline
                    rows={2}
                    {...register('description')}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Contato */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Contato
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Telefone"
                    fullWidth
                    {...register('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Website"
                    type="url"
                    fullWidth
                    {...register('website')}
                    error={!!errors.website}
                    helperText={errors.website?.message || 'https://exemplo.com'}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Endereço */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Endereço
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField
                    label="Rua/Avenida"
                    fullWidth
                    {...register('addressStreet')}
                    error={!!errors.addressStreet}
                    helperText={errors.addressStreet?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Número"
                    fullWidth
                    {...register('addressNumber')}
                    error={!!errors.addressNumber}
                    helperText={errors.addressNumber?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Complemento"
                    fullWidth
                    {...register('addressComplement')}
                    error={!!errors.addressComplement}
                    helperText={errors.addressComplement?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Bairro"
                    fullWidth
                    {...register('addressNeighborhood')}
                    error={!!errors.addressNeighborhood}
                    helperText={errors.addressNeighborhood?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    label="Cidade"
                    fullWidth
                    {...register('addressCity')}
                    error={!!errors.addressCity}
                    helperText={errors.addressCity?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    label="UF"
                    fullWidth
                    {...register('addressState')}
                    error={!!errors.addressState}
                    helperText={errors.addressState?.message || 'SP'}
                    disabled={loading}
                    inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="CEP"
                    fullWidth
                    {...register('addressZipCode')}
                    error={!!errors.addressZipCode}
                    helperText={errors.addressZipCode?.message || '12345-678'}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Alert de validação */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid || (!isDirty && !isEdit)}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
